/**
 * EquipmentSpecificationSection
 * 
 * Renders equipment specification tables with 3-column layout.
 * Handles both main equipment and sub-equipment.
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG } from '../../config'
import { TableRenderer, type ControlCardEquipment, type PageHeaderConfig, type ContinuationHeaderCallback } from '../../infrastructure'

/**
 * Equipment specification section renderer
 */
export class EquipmentSpecificationSection {
  private readonly tableRenderer: TableRenderer
  private readonly config: typeof CONTROL_CARD_CONFIG

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
    this.tableRenderer = new TableRenderer(config)
  }

  /**
   * Render equipment specifications
   * Returns the Y position after rendering
   */
  async render(
    doc: jsPDF,
    equipment: ControlCardEquipment,
    startY: number,
    pageHeader?: PageHeaderConfig,
    continuationHeaderCallback?: ContinuationHeaderCallback
  ): Promise<number> {
    // Skip if no specifications
    if (equipment.specifications.isEmpty()) {
      return startY
    }

    // Get display rows (3-column layout)
    const displayRows = equipment.specifications.toDisplayRows(3)

    // Render specification table
    const finalY = await this.tableRenderer.renderSpecificationTable(
      doc,
      equipment.name, // Equipment header
      displayRows,
      startY,
      this.config.layout.tableStartX,
      pageHeader,
      continuationHeaderCallback
    )

    return finalY
  }

  /**
   * Render all equipment (main + sub-equipment)
   * Returns the Y position after rendering
   */
  async renderAll(
    doc: jsPDF,
    mainEquipment: ControlCardEquipment,
    subEquipment: ControlCardEquipment[],
    startY: number,
    pageHeader?: PageHeaderConfig,
    continuationHeaderCallback?: ContinuationHeaderCallback
  ): Promise<number> {
    let currentY = startY

    // Render main equipment
    currentY = await this.render(doc, mainEquipment, currentY, pageHeader, continuationHeaderCallback)

    // Render sub-equipment
    for (const subEquip of subEquipment) {
      currentY = await this.render(doc, subEquip, currentY, pageHeader, continuationHeaderCallback)
    }

    return currentY
  }
}
